import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, Check, Building2, Globe, Briefcase, LayoutGrid, ArrowRight, ArrowLeft } from "lucide-react";

const STEPS = [
  { id: 1, label: "Entreprise", icon: Building2 },
  { id: 2, label: "Pays & Devise", icon: Globe },
  { id: 3, label: "Secteur", icon: Briefcase },
  { id: 4, label: "Modules", icon: LayoutGrid },
];

export default function Onboarding() {
  const { refresh, switchCompany, companies } = useApp();
  const navigate = useNavigate();
  const [ref, setRef] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    legal_name: "", trade_name: "", tax_id: "",
    country_code: "BF", base_currency: "XOF", sector: "commerce", modules: [],
  });

  useEffect(() => {
    api.get("/reference").then(({ data }) => {
      setRef(data);
      setForm((f) => ({ ...f, modules: data.modules.filter((m) => m.default).map((m) => m.code) }));
    });
  }, []);

  useEffect(() => {
    if (companies.length > 0) navigate("/dashboard", { replace: true });
  }, [companies, navigate]);

  const pickCountry = (code) => {
    const c = ref.countries.find((x) => x.code === code);
    setForm({ ...form, country_code: code, base_currency: c?.currency || form.base_currency });
  };

  const toggleModule = (code) => {
    setForm((f) => ({ ...f, modules: f.modules.includes(code) ? f.modules.filter((m) => m !== code) : [...f.modules, code] }));
  };

  const canNext = () => {
    if (step === 1) return form.legal_name.trim().length >= 2;
    return true;
  };

  const finish = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/companies", form);
      await refresh();
      switchCompany(data.company.id);
      toast.success("Entreprise créée avec succès !");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!ref) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  return (
    <div className="min-h-screen bg-background py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="font-display text-2xl font-extrabold mb-8">Baob<span className="text-primary">ab</span></div>

        <div className="flex items-center gap-2 mb-8" data-testid="onboarding-steps">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            const active = step === s.id, done = step > s.id;
            return (
              <div key={s.id} className="flex items-center flex-1">
                <div className={`flex items-center gap-2 ${active || done ? "text-primary" : "text-muted-foreground"}`}>
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center border ${done ? "bg-primary text-white border-primary" : active ? "border-primary" : "border-border"}`}>
                    {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                  </div>
                  <span className="text-sm font-medium hidden sm:block">{s.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={`h-px flex-1 mx-2 ${done ? "bg-primary" : "bg-border"}`} />}
              </div>
            );
          })}
        </div>

        <Card className="p-6 sm:p-8 rounded-md border-border shadow-sm">
          {step === 1 && (
            <div className="space-y-5" data-testid="step-company">
              <h2 className="font-display text-2xl font-bold">Votre entreprise</h2>
              <div className="space-y-2">
                <Label>Nom légal *</Label>
                <Input value={form.legal_name} onChange={(e) => setForm({ ...form, legal_name: e.target.value })} placeholder="Ma Boutique SARL" data-testid="company-legal-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Nom commercial</Label>
                <Input value={form.trade_name} onChange={(e) => setForm({ ...form, trade_name: e.target.value })} placeholder="Ma Boutique" data-testid="company-trade-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Numéro fiscal / IFU</Label>
                <Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })} placeholder="Optionnel" data-testid="company-tax-id-input" />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5" data-testid="step-country">
              <h2 className="font-display text-2xl font-bold">Pays & devise</h2>
              <div className="space-y-2">
                <Label>Pays</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {ref.countries.map((c) => (
                    <button key={c.code} type="button" onClick={() => pickCountry(c.code)}
                      className={`p-3 rounded-md border text-left text-sm transition-colors ${form.country_code === c.code ? "border-primary bg-accent" : "border-border hover:bg-secondary"}`}
                      data-testid={`country-${c.code}`}>
                      <div className="font-semibold">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.currency} · {c.framework}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label>Devise</Label>
                <div className="flex flex-wrap gap-2">
                  {ref.currencies.map((c) => (
                    <button key={c.code} type="button" onClick={() => setForm({ ...form, base_currency: c.code })}
                      className={`px-4 py-2 rounded-full border text-sm ${form.base_currency === c.code ? "border-primary bg-primary text-white" : "border-border hover:bg-secondary"}`}
                      data-testid={`currency-${c.code}`}>{c.code} · {c.symbol}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-5" data-testid="step-sector">
              <h2 className="font-display text-2xl font-bold">Votre secteur d'activité</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ref.sectors.map((s) => (
                  <button key={s.code} type="button" onClick={() => setForm({ ...form, sector: s.code })}
                    className={`p-3 rounded-md border text-left text-sm transition-colors ${form.sector === s.code ? "border-primary bg-accent" : "border-border hover:bg-secondary"}`}
                    data-testid={`sector-${s.code}`}>{s.name}</button>
                ))}
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="space-y-5" data-testid="step-modules">
              <h2 className="font-display text-2xl font-bold">Modules à activer</h2>
              <p className="text-sm text-muted-foreground">Vous pourrez les modifier plus tard.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {ref.modules.map((m) => {
                  const on = form.modules.includes(m.code);
                  return (
                    <button key={m.code} type="button" onClick={() => toggleModule(m.code)}
                      className={`p-3 rounded-md border flex items-center justify-between text-sm transition-colors ${on ? "border-primary bg-accent" : "border-border hover:bg-secondary"}`}
                      data-testid={`module-${m.code}`}>
                      <span className="font-medium">{m.name}</span>
                      <span className={`w-5 h-5 rounded flex items-center justify-center border ${on ? "bg-primary border-primary text-white" : "border-border"}`}>{on && <Check className="w-3 h-3" />}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8">
            <Button variant="ghost" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} data-testid="onboarding-back-button">
              <ArrowLeft className="w-4 h-4 mr-1" /> Retour
            </Button>
            {step < 4 ? (
              <Button onClick={() => canNext() && setStep((s) => s + 1)} disabled={!canNext()} className="rounded-full active:scale-95 transition-transform" data-testid="onboarding-next-button">
                Suivant <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={finish} disabled={loading} className="rounded-full active:scale-95 transition-transform" data-testid="onboarding-finish-button">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Terminer <Check className="w-4 h-4 ml-1" /></>}
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
